/**
 * Capture-session orchestration: wires the sync engine, the mic capture engine, the session
 * state machine and callout-api into one hook the Capture route drives.
 *
 * Ownership boundary: this hook walks the session state machine, but the WORK behind
 * `transcribing` / `structuring` / `rendering` belongs to the PIPELINE and RENDER cards. Until
 * those workers land, `advancePipeline()` steps the machine (and persists each status to D1)
 * so capture → complete is observable end to end.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MicError, MicCapture, type MicMode } from '../capture';
import {
  ApiError,
  createSession as createSessionRecord,
  structureSession,
  transcribeSession,
  uploadAudioChunk,
  updateSession,
  type AudioChunkRecord,
  type StructuredClipRow,
  type TaggingSession,
} from '../api/client';
import { attachMediaElement, SyncEngine } from '../sync/engine';
import { SessionMachine, canTransition, type TransitionEvent } from './machine';
import { useSessionStore, type ClipProposal, type SessionStatus } from './store';
import { rememberReviewSession } from './review';
import { useTokenSource } from '../api/token';

export interface CaptureSessionApi {
  videoRef: React.RefObject<HTMLVideoElement>;
  syncEngine: SyncEngine;
  status: SessionStatus;
  micMode: MicMode;
  micState: string;
  segments: number;
  chunks: AudioChunkRecord[];
  busy: boolean;
  error: string | null;
  startSession: () => Promise<void>;
  endSession: () => Promise<void>;
  newSession: () => void;
  advancePipeline: () => Promise<void>;
  toggleMic: () => Promise<void>;
  setMicMode: (mode: MicMode) => void;
  seekTo: (seconds: number) => void;
  pushTranscript: (text: string, wallStartMs: number, durationMs: number) => void;
}

/** Maps a D1 clip row onto the capture store's proposal shape (the deck reads D1 directly). */
function toClipProposal(clip: StructuredClipRow): ClipProposal {
  return {
    id: clip.id,
    tIn: clip.t_in,
    tOut: clip.t_out,
    label: clip.label,
    vocabRef: clip.vocab_ref,
    notes: clip.notes,
    confidence: clip.confidence,
    status: clip.status,
  };
}

/**
 * Open-mic chunk length is left at the recorder's own default (30 s).
 *
 * MEASURED, do not shorten it: with `chunkMs: 10_000` in this Chrome the first timeslice of an
 * audio-only stream is a header-only WebM blob, `decodeAudioData` refuses it and
 * web/src/capture/recorder.ts correctly falls back to deferred one-piece processing — so NOTHING
 * is uploaded until the coach ends the session (observed on the deployed URL: 0 audio chunks in
 * 75 s of open mic). The transcription worker emits ONE transcript segment per audio part, which
 * is why the pipeline E2E's microphone fixture is 90 s carrying three clock-referenced moments
 * (tests/fixtures/test-coach-90s.wav): three 30 s parts → three segments → three clips.
 */

export function useCaptureSession(): CaptureSessionApi {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const engine = useMemo(() => new SyncEngine(), []);
  const micRef = useRef<MicCapture | null>(null);
  const chunksRef = useRef<AudioChunkRecord[]>([]);
  const sessionRef = useRef<TaggingSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chunks, setChunks] = useState<AudioChunkRecord[]>([]);
  const tokens = useTokenSource();

  const store = useSessionStore;
  const status = useSessionStore((s) => s.status);
  const micMode = useSessionStore((s) => s.micMode);
  const micState = useSessionStore((s) => s.micState);
  const footageId = useSessionStore((s) => s.footageId);
  const sessionId = useSessionStore((s) => s.sessionId);
  const segments = useSessionStore((s) => s.segments);

  const machine = useMemo(
    () =>
      new SessionMachine('idle', (event: TransitionEvent) => {
        store.getState().setStatus(event.to);
        store.getState().noteTransition({
          from: event.from,
          to: event.to,
          at: event.at,
          reason: event.reason,
        });
      }),
    [store],
  );

  // Anchor the engine to the real video element. The player only exists once footage is
  // uploaded, so this must re-run when footage arrives — not just on mount.
  const footageForPlayer = useSessionStore((s) => s.footage);
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    return attachMediaElement(engine, video);
  }, [engine, footageForPlayer]);

  const persistStatus = useCallback(
    async (next: SessionStatus, extra: Parameters<typeof updateSession>[1] = {}) => {
      const id = sessionRef.current?.id ?? sessionId;
      if (!id) return;
      const token = await tokens.getToken();
      await updateSession(id, { status: next as never, ...extra }, token);
    },
    [sessionId, tokens],
  );

  const startSession = useCallback(async () => {
    if (!footageId) {
      setError('Upload footage before starting a session.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const token = await tokens.getToken();
      const capabilityReport = store.getState().capabilityReport;
      const { session } = await createSessionRecord(footageId, token, { capabilityReport });
      sessionRef.current = session;
      store.getState().setSession(session.id);
      machine.transition('recording', 'coach started the session');

      const mic = new MicCapture({
        mode: store.getState().micMode,
        upload: async (wav, meta) => {
          const chunk = await uploadAudioChunk(session.id, wav, meta, token);
          chunksRef.current = [...chunksRef.current, chunk];
          setChunks(chunksRef.current);
          return chunk;
        },
        mapWallToVideo: (wallMs) => engine.mapAudioToVideo(wallMs),
        anchorSegment: (startWallMs, endWallMs) => {
          // Audit-trail samples only; the session origin is set when the video element attaches.
          const startS = engine.mapAudioToVideo(startWallMs);
          const endS = engine.mapAudioToVideo(endWallMs);
          if (startS === null || endS === null) return;
          engine.anchor(startS, startWallMs);
          engine.anchor(endS, endWallMs);
        },
        onSegment: (segment) => store.getState().upsertSegment({ ...segment }),
        onStateChange: (state) => store.getState().setMicState(state),
        onError: (micError) => {
          // A failed chunk upload must not relabel the mic: the engine's own state (which drives
          // micState via onStateChange) is the truth. Surface the message and let the coach retry.
          setError(micError.message);
          if (micError.failure !== 'unknown') store.getState().setMicState('error', micError.message);
        },
      });
      micRef.current = mic;
      await mic.start();
    } catch (caught) {
      const message = caught instanceof MicError ? caught.message : String(caught);
      setError(message);
      machine.transition('error', message);
    } finally {
      setBusy(false);
    }
  }, [engine, footageId, machine, store, tokens]);

  /**
   * The pipeline the SPA never ran: recorded audio → transcript → clip proposals.
   *
   * `POST /api/sessions/:id/transcribe` reads the uploaded WAV chunks out of R2 and returns the
   * transcript already projected onto video time through the wall-clock anchors; the lines are
   * pushed into the store, which is what the live timeline renders as
   * `[data-testid="transcript-segment"]`. `POST /api/sessions/:id/structure` then turns that
   * transcript into the clip proposals the review deck shows. Both calls carry the stored bearer.
   *
   * State transitions are guarded by `canTransition`, so a manual re-run from `review` does the
   * work without tripping the machine.
   */
  const runPipeline = useCallback(
    async (
      id: string,
      token: string | null,
      opts: { chunks?: AudioChunkRecord[] } = {},
    ): Promise<{ segments: number; clips: StructuredClipRow[] }> => {
      const chunks = opts.chunks ?? chunksRef.current;
      const step = (to: SessionStatus, reason: string) => {
        if (canTransition(machine.current, to)) machine.transition(to, reason);
      };

      const transcribed = await transcribeSession(id, token, { chunks });
      const segments = transcribed.transcript?.segments ?? [];
      store.getState().setTranscriptSegments(
        segments.map((segment) => ({
          text: segment.text,
          startS: segment.start_s,
          endS: segment.end_s,
        })),
      );
      step('structuring', `transcript: ${segments.length} line(s) from ${chunks.length} chunk(s)`);
      await persistStatus('structuring', {
        transcriptRaw: transcribed.transcript?.text ?? '',
        transcriptTimeline: segments.map((segment) => ({
          text: segment.text,
          startS: segment.start_s,
          endS: segment.end_s,
        })),
      });

      // The structuring worker refuses an empty transcript — surface that rather than asking and
      // collecting a 422 the coach cannot act on.
      if (segments.length === 0) {
        throw new ApiError(
          422,
          'no_transcript',
          transcribed.message ??
            'No speech was transcribed for this session — there are no taggable moments.',
        );
      }

      const structured = await structureSession(id, token, { transcript: segments });
      const clips = structured.clips ?? [];
      store.getState().setClips(clips.map(toClipProposal));
      step('review', `structuring proposed ${clips.length} clip(s)`);
      await persistStatus('review');
      // So a full page load of /review restores THIS session without the coach pasting an id.
      rememberReviewSession(id);
      return { segments: segments.length, clips };
    },
    [machine, persistStatus, store],
  );

  const endSession = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const mic = micRef.current;
      if (mic) {
        const captured = await mic.stop();
        store.getState().setSegments([...captured]);
      }
      micRef.current = null;

      const id = sessionRef.current?.id ?? sessionId;
      if (!id) throw new Error('No session to end — start a session first.');
      const token = await tokens.getToken();
      const anchorPairs = engine.anchorPairs.map((pair) => ({ ...pair }));

      machine.transition('transcribing', 'session ended — transcript pipeline triggered');
      // Persists the chunk manifest to R2 as well as the anchors, so the transcript is anchored
      // even if the explicit `chunks` list is ever dropped.
      await persistStatus('transcribing', { audioChunks: chunksRef.current, anchorPairs });

      await runPipeline(id, token, { chunks: chunksRef.current });
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : String(caught);
      setError(message);
      if (canTransition(machine.current, 'error')) machine.transition('error', message);
    } finally {
      setBusy(false);
    }
  }, [engine, machine, persistStatus, runPipeline, sessionId, store, tokens]);

  /**
   * Manual re-run of the pipeline (the "Analyse" button): transcribe whatever audio the session
   * has and ask for clip proposals again. Used after a capture that ended without the pipeline,
   * or to re-analyse after the coach recorded more.
   */
  const advancePipeline = useCallback(async () => {
    const id = sessionRef.current?.id ?? sessionId;
    if (!id) {
      setError('Start a session before running the pipeline.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const token = await tokens.getToken();
      await runPipeline(id, token, { chunks: chunksRef.current });
    } catch (caught) {
      const message = caught instanceof ApiError ? caught.message : String(caught);
      setError(message);
      if (canTransition(machine.current, 'error')) machine.transition('error', message);
    } finally {
      setBusy(false);
    }
  }, [machine, runPipeline, sessionId, tokens]);

  const toggleMic = useCallback(async () => {
    const mic = micRef.current;
    if (!mic) {
      setError('Start the session before using the microphone.');
      return;
    }
    await mic.toggle();
    store.getState().setSegments([...mic.capturedSegments]);
  }, [store]);

  const setMicMode = useCallback(
    (mode: MicMode) => {
      store.getState().setMicMode(mode);
    },
    [store],
  );

  /** Starts a fresh session against the same footage (after complete/error). */
  const newSession = useCallback(() => {
    micRef.current = null;
    chunksRef.current = [];
    sessionRef.current = null;
    setChunks([]);
    setError(null);
    store.getState().resetSession();
    machine.transition('idle', 'coach started a new session');
  }, [machine, store]);

  const seekTo = useCallback((seconds: number) => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = Math.max(0, seconds);
  }, []);

  /**
   * Feed for the live transcript. The PIPELINE card's transcription proxy calls this with the
   * wall-clock start of the chunk the words came from; the sync engine maps it onto the video.
   */
  const pushTranscript = useCallback(
    (text: string, wallStartMs: number, durationMs: number) => {
      const startS = engine.mapAudioToVideo(wallStartMs);
      if (startS === null) return;
      store.getState().pushTranscriptSegment({
        text,
        startS,
        endS: startS + durationMs / 1000,
      });
    },
    [engine, store],
  );

  /**
   * E2E / integration seam. `window.__callout` exposes the live engine, machine and store so a
   * harness can drive the capture pipeline without a real transcription backend (and so the
   * PIPELINE card has one place to hook the transcript feed).
   */
  useEffect(() => {
    const seam = {
      engine,
      machine,
      store,
      mic: () => micRef.current,
      pushTranscript,
      chunks: () => chunksRef.current,
      syncProbe: () => {
        const video = videoRef.current;
        return {
          videoTime: video?.currentTime ?? null,
          wallTime: performance.now(),
          mapped: video ? engine.mapAudioToVideo(performance.now()) : null,
          anchors: engine.anchorPairs.length,
          pauseMs: engine.totalPauseDurationMs,
        };
      },
    };
    (window as unknown as { __callout?: unknown }).__callout = seam;
    return () => {
      delete (window as unknown as { __callout?: unknown }).__callout;
    };
  }, [engine, machine, pushTranscript, store]);

  return {
    videoRef,
    syncEngine: engine,
    status,
    micMode,
    micState,
    segments: segments.length,
    chunks,
    busy,
    error,
    startSession,
    endSession,
    newSession,
    advancePipeline,
    toggleMic,
    setMicMode,
    seekTo,
    pushTranscript,
  };
}
